import { RestResponse } from '../../api/Rest';
import {
  acceptNameCardTransfer as restAcceptNameCardTransfer,
  CollectedNameCardData,
  collectNameCard as restCollectNameCard,
  createDonation,
  NameCardData,
  updateCurrentUser as restUpdateCurrentUser,
} from '../../api/Rest/user';
import {
  createSystemInvitation as restCreateSystemInvitation,
  FindProfileData,
  PhoneParams,
  ResetPassswordResponse,
  searchProfiles,
  updateSystemInvitation as restUpdateSystemInvitation,
  VerifiedPhoneParams,
} from '../../api/Rest/users';
import { ToAttachmentWithPath } from '../../api/utils';
import { UserId, Uuid } from '../Base';
import Repository, { Cursor, RepositoryJson } from '../Repository';
import { SpaceMembership } from '../SpaceStore';
import { AsyncActionOptions, FetchActionResult } from '../Store';
import { FirstParameter, NonEmptyArray, SecondParameter } from '../Utils';

import { Account, AccountJson, Password } from './Account';
import { Allowance, AllowanceJson } from './Allowance';
import {
  Invitation,
  InvitationJson,
  InvitationLink,
  InvitationLinkJson,
  InvitationType,
  RedemptionCode,
  RedemptionCodeJson,
  SystemInvitation,
  SystemInvitationJson,
  SystemInvitationType,
} from './Invitation';
import { NameCard, NameCardJson } from './NameCard';
import { PhoneVerificationJson } from './PhoneVerification';
import { Profile, ProfileJson } from './Profile';

const USER_CACHE_LIMIT = 1000;
const RECENT_LOGIN_CACHE_LIMIT = 8;

export enum ResetPasswordResult {
  Success = 'success',
  Error = 'error',
}

export enum UserListType {
  Current = 'current', // current userId
  Main = 'main', // main account's userId
  Sub = 'sub', // subaccounts' userIds
  RecentLogin = 'recentLogin', // recentLogin's userIds
  ShareContent = 'shareContent', //share content related
  Referral = 'referral', // invitations releated

  Recent = 'Recent', //usage: post display
}
export enum RedemptionCodeListType {
  Recent = 'Recent',
}

export enum ProfileMapType {
  SpaceRoot = 'spaceRoot', // classroomId -> space root's profile
  SpaceMembershipUser = 'spaceMembershipUser', // classroomId_userId? -> space member's profile
  Redemption = 'Redemption', // redemptionId -> userId's profile
  RewardOffer = 'RewardOffer', // rewardOfferId -> createdBy's profile
}

export enum ProfileListType {
  FindProfile = 'findProfile',
  SearchResult = 'searchResult',
  ClaimExpRewards = 'claimExpRewards',
  RecommendedUser = 'recommendedUser',
}

export enum NameCardListType {
  Collect = 'collect',
}

export enum UpdateCurrentUserResult {
  Success = 'success',
  Error = 'error',
  UsernameAlreadyRegistered = 'usernameAlreadyRegistered', // from errors.code
  EmailAlreadyRegistered = 'emailAlreadyRegistered', // from errors.code
  PhoneAlreadyRegistered = 'phone number already registered', // from errors.message
  InvalidCredentials = 'incorrect password', // from errors.message
  UsernameIncludingKeyword = 'username includes official keyword', // from errors.message
}

interface PhoneVerificationRespond {
  phoneVerification: PhoneVerificationJson | null;
  result: UpdateCurrentUserResult;
}

export type ProfileMapField = ProfileMapType;
export type ProfileListField = string | UserListType | ProfileListType;
export class ProfileRepository extends Repository<
  Profile,
  ProfileJson<Date | number>,
  ProfileMapField,
  ProfileListField
> {
  constructor(
    repository?: RepositoryJson<
      ProfileJson<Date | number>,
      ProfileMapField,
      ProfileListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [ProfileMapType.SpaceRoot]: {},
          [ProfileMapType.SpaceMembershipUser]: {},
        }),
        listFields: Repository.initFields(repository?.listFields, {
          [UserListType.Current]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.Main]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.Sub]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.RecentLogin]: { cacheLimit: RECENT_LOGIN_CACHE_LIMIT },
          [UserListType.ShareContent]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.Recent]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.Referral]: { cacheLimit: USER_CACHE_LIMIT },
        }),
      },
      (x) => new Profile(x),
    );
  }
}

export type AccountMapField = string;
export type AccountListField = string | UserListType;
export class AccountRepository extends Repository<
  Account,
  AccountJson<Date | number>,
  AccountMapField,
  AccountListField
> {
  constructor(
    repository?: RepositoryJson<
      AccountJson<Date | number>,
      AccountMapField,
      AccountListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [UserListType.Current]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.Main]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.Sub]: { cacheLimit: USER_CACHE_LIMIT },
          [UserListType.RecentLogin]: { cacheLimit: RECENT_LOGIN_CACHE_LIMIT },
        }),
      },
      (x) => new Account(x),
    );
  }
}

export type InvitationMapField = string;
export type InvitationListField = string;
export class InvitationRepository extends Repository<
  Invitation,
  InvitationJson<Date | number>,
  InvitationMapField,
  InvitationListField
> {
  constructor(
    repository?: RepositoryJson<
      InvitationJson<Date | number>,
      InvitationMapField,
      InvitationListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [InvitationType.Referral]: {},
          [InvitationType.SecondReferral]: {},
        }),
        mapFields: Repository.initFields(repository?.mapFields, {
          [InvitationType.Referral]: {},
          [InvitationType.SecondReferral]: {},
        }),
      },
      (x) => new Invitation(x),
    );
  }
}

export type SystemInvitationMapField = string;
export type SystemInvitationListField = string;
export class SystemInvitationRepository extends Repository<
  SystemInvitation,
  SystemInvitationJson<Date | number>,
  SystemInvitationMapField,
  SystemInvitationListField
> {
  constructor(
    repository?: RepositoryJson<
      SystemInvitationJson<Date | number>,
      SystemInvitationMapField,
      SystemInvitationListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [SystemInvitationType.JoinClub]: {},
          [SystemInvitationType.ReservedName]: {},
        }),
        mapFields: Repository.initFields(repository?.mapFields, {
          [SystemInvitationType.JoinClub]: {},
          [SystemInvitationType.ReservedName]: {},
        }),
      },
      (x) => new SystemInvitation(x),
    );
  }
}

export type InvitationLinkMapField = string;
export type InvitationLinkListField = string;
export class InvitationLinkRepository extends Repository<
  InvitationLink,
  InvitationLinkJson<Date | number>,
  InvitationLinkMapField,
  InvitationLinkListField
> {
  constructor(
    repository?: RepositoryJson<
      InvitationLinkJson<Date | number>,
      InvitationLinkMapField,
      InvitationLinkListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [UserListType.ShareContent]: {},
        }),
      },
      (x) => new InvitationLink(x),
    );
  }
}

export type RedemptionCodeMapField = string;
export type RedemptionCodeListField = string;
export class RedemptionCodeRepository extends Repository<
  RedemptionCode,
  RedemptionCodeJson<Date | number>,
  RedemptionCodeMapField,
  RedemptionCodeListField
> {
  constructor(
    repository?: RepositoryJson<
      RedemptionCodeJson<Date | number>,
      RedemptionCodeMapField,
      RedemptionCodeListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [RedemptionCodeListType.Recent]: {},
        }),
      },
      (x) => new RedemptionCode(x),
    );
  }
}

export type NameCardMapField = string;
export type NameCardListField = string;
export class NameCardRepository extends Repository<
  NameCard,
  NameCardJson<Date | number>,
  NameCardMapField,
  NameCardListField
> {
  constructor(
    repository?: RepositoryJson<
      NameCardJson<Date | number>,
      NameCardMapField,
      NameCardListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [NameCardListType.Collect]: {},
        }),
      },
      (x) => new NameCard(x),
    );
  }
}

export type UserJson<T = number> = {
  account: AccountJson<T>;
  profile: ProfileJson<T>;
};

export type User = {
  account?: Account;
  profile?: Profile;
};

export type ProfileWithMemberships = {
  profile: Profile;
  membership: SpaceMembership | null;
};

export interface UserStoreStateJson {
  profiles: RepositoryJson<
    ProfileJson,
    ProfileMapField,
    ProfileListField,
    number
  >;
  accounts: RepositoryJson<
    AccountJson,
    AccountMapField,
    AccountListField,
    number
  >;
  invitations: RepositoryJson<
    InvitationJson,
    InvitationMapField,
    InvitationListField,
    number
  >;
  systemInvitations: RepositoryJson<
    SystemInvitationJson,
    SystemInvitationMapField,
    SystemInvitationListField,
    number
  >;
  invitationLinks: RepositoryJson<
    InvitationLinkJson,
    InvitationLinkMapField,
    InvitationLinkListField,
    number
  >;
  redemptionCodes: RepositoryJson<
    RedemptionCodeJson,
    RedemptionCodeMapField,
    RedemptionCodeListField,
    number
  >;
  allowance?: AllowanceJson;
}

export interface UserStoreState {
  profiles: ProfileRepository;
  accounts: AccountRepository;
  invitations: InvitationRepository;
  systemInvitations: SystemInvitationRepository;
  invitationLinks: InvitationLinkRepository;
  redemptionCodes: RedemptionCodeRepository;
  nameCards: NameCardRepository;
  allowance?: Allowance;
}

export interface UserStoreAction {
  setupActiveUserInfo: (
    current?: Required<UserJson>,
    main?: UserJson,
    subs?: UserJson[],
  ) => void;
  setProfiles: (
    profiles: Profile | Profile[],
    listFields: AccountListField | NonEmptyArray<AccountListField>,
  ) => void;
  addProfiles: (
    listFields: ProfileListField | ProfileListField[],
    ...profiles: Profile[]
  ) => void;
  setAccounts: (
    accounts: Account | Account[],
    listFields: AccountListField | NonEmptyArray<AccountListField>,
  ) => void;
  addAccounts: (
    listFields: AccountListField | AccountListField[],
    ...accounts: Account[]
  ) => void;
  deleteProfileFromMap: (fieldName: ProfileMapField, id: string) => void;
  getProfile: (userId: string) => Profile | null | undefined;
  getAccount: (userId: string) => Account | null | undefined;
  getProfiles: (fieldName: ProfileListField) => Profile[];
  getProfileFromMap: (
    fieldName: ProfileMapField,
    key: string,
  ) => Profile | null | undefined;
  addProfilesWithMemberships: (
    mapFields: ProfileMapField | ProfileMapField[],
    profiles: Record<string, ProfileWithMemberships>,
  ) => void;
  getCurrentUser: (options?: AsyncActionOptions) => Promise<void>;
  getAllowance: () => Allowance | undefined;
  fetchAllowance: (options?: AsyncActionOptions) => Promise<void>;
  sendPhoneVerification: (
    params: PhoneParams,
    options?: AsyncActionOptions,
  ) => Promise<PhoneVerificationRespond>;
  validatePhone: (
    params: VerifiedPhoneParams,
    options?: AsyncActionOptions,
  ) => Promise<ResetPasswordResult>;
  verifyEmail: (
    params: { email: string; token: string },
    options?: AsyncActionOptions,
  ) => Promise<ResetPasswordResult>;
  resetPasswordValidation: (
    params: { email: string; token: string },
    options?: AsyncActionOptions,
  ) => Promise<ResetPasswordResult>;
  resetPassword: (
    params:
      | { password: string; email: string; token: string }
      | { password: string; phone: VerifiedPhoneParams },
    options?: AsyncActionOptions,
  ) => Promise<ResetPassswordResponse | undefined>;
  forgotPassword: (
    params: { userId?: string; username?: string; email?: string },
    options?: AsyncActionOptions,
  ) => Promise<Password | null>;
  forgotPasswordWithPhone: (
    params: {
      userId?: string;
      username?: string;
      email?: string;
      phone?: PhoneParams;
    },
    options?: AsyncActionOptions,
  ) => Promise<PhoneVerificationJson | null>;
  findProfile: (
    params: { username?: string; email?: string; phone?: PhoneParams },
    options?: AsyncActionOptions,
  ) => Promise<RestResponse<FindProfileData>>;
  sendVerification: (
    params: { userId?: string; username?: string; email?: string },
    options?: AsyncActionOptions,
  ) => Promise<ResetPasswordResult>;
  userCodeValidation: (
    params: { userCode: string },
    options?: AsyncActionOptions,
  ) => Promise<ResetPasswordResult>;
  getInvitation: (userId: string) => Invitation | null | undefined;
  getInvitations: (fieldName: string) => Invitation[];
  fetchInvitations: (
    params: {
      type: InvitationType;
      invitedBy?: Uuid;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchInvitation: (options?: AsyncActionOptions) => Promise<void>;
  fetchInvitationUser: (
    userId: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  getSystemInvitation: (
    systemInvitationId: string,
  ) => SystemInvitation | null | undefined;
  getSystemInvitations: (fieldName: string) => SystemInvitation[];
  fetchSystemInvitations: (
    params: {
      type: SystemInvitationType;
      reverse: boolean;
      followedUpBy?: UserId | UserId[];
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchSystemInvitation: (
    systemInvitationId: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  createSystemInvitation: (
    body: FirstParameter<typeof restCreateSystemInvitation>,
    options?: AsyncActionOptions,
  ) => Promise<string | undefined>;
  updateSystemInvitation: (
    systemInvitationId: string,
    body: SecondParameter<typeof restUpdateSystemInvitation>,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  deleteSystemInvitation: (
    systemInvitationId: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  sentInvitationEmail: (
    systemInvitationId: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  acceptInvitationEmail: (
    systemInvitationId: string,
    body: { classroomIds?: Uuid[] },
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  sentPasswordEmail: (
    systemInvitationId: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  getInvitationLink: (
    invitationLinkId: string,
  ) => InvitationLink | null | undefined;
  fetchInvitationLink: (
    invitationLinkId: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  getRedemptionCode: (code: string) => RedemptionCode | null | undefined;
  fetchRedemptionCode: (
    redemptionCode: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;

  // api
  searchProfiles: (
    params: FirstParameter<typeof searchProfiles>,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  // nameCard
  getNameCard: (nameCardId: string) => NameCard | null | undefined;
  fetchNameCard: (
    nameCardId: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  collectNameCard: (
    nameCardId: string,
    body: SecondParameter<typeof restCollectNameCard>,
    options?: AsyncActionOptions,
  ) => Promise<RestResponse<CollectedNameCardData>>;
  acceptNameCardTransfer: (
    nameCardId: string,
    body: SecondParameter<typeof restAcceptNameCardTransfer>,
    options?: AsyncActionOptions,
  ) => Promise<RestResponse<NameCardData>>;
  updateCurrentUser: (
    body: ToAttachmentWithPath<FirstParameter<typeof restUpdateCurrentUser>>,
    options?: AsyncActionOptions,
  ) => Promise<UpdateCurrentUserResult>;

  createDonation: (
    body: FirstParameter<typeof createDonation>,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
}
